import {
  BaseMention,
  CustomLink,
  CustomParagraph,
  CustomSlateElement,
  CustomText
} from '../../../../models/slate-editor';
import { Element, Text } from 'slate';
import { jsx } from 'slate-hyperscript';
import { allLinksInText, linkMatcher, LinkType } from './withLinks';
import { SocialNetworksEnum } from '../../../../constants/social-networks';
import { typeGuards } from './typeGuards';
import { PostEditorLink } from '../../../../models/post-editor';
import DOMPurify from 'dompurify';
import { ThirdPartyProfileSearchType } from '../../../../models/graphql';

const htmlDecode = (text: string): string => {
  const textArea = document.createElement('textarea');
  textArea.innerHTML = text;
  return textArea.value;
};

const escapeRegExp = (string: string) => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string

const emptyChar = `&nbsp;`;
const emptyCharRegex = new RegExp(`^(${emptyChar}|\\s+${emptyChar})+|(${emptyChar}|${emptyChar}\\s+)+$`, 'g');

export const deserializeText = (
  text: string,
  mentions: BaseMention[],
  link: PostEditorLink | null,
  thirdParty = false
): CustomParagraph[] => {
  const arrayText = text.split(/(\r\n|\r|\n)/).filter(line => line !== '\r' && line !== '\n' && line !== '\r\n');
  const paragraphs = arrayText.map(
    text => jsx('element', { type: 'paragraph' }, [{ text: htmlDecode(text) }])
    // ^HtmlDecode TODO Temp Fix to allow parsing of html entities such as &amp; &nbsp; etc.
  );
  if (!typeGuards.isCustomParagraphArrayGuard(paragraphs))
    throw new Error('paragraphs are not of type CustomParagraph');
  return paragraphs.map(p => {
    const children = p.children.map(child => {
      if (Text.isText(child)) {
        const linksInText = allLinksInText(child.text);
        let linkInText: LinkType | null;
        if (link) {
          linkInText =
            linksInText.find(({ url }) => {
              return thirdParty ? url === link.initialUrl : url === link.url;
            }) || null;
        } else {
          linkInText = linkMatcher(child.text);
        }
        const isTwitter = !!mentions.find(mention => mention.platform === 'twitter');
        const mention = isTwitter ? child.text.match(/@(\w+)/) : child.text.match(/@\[(\w+)]/);
        if (linkInText) {
          const { text: linkText, url } = linkInText;
          const regex = new RegExp(`(${escapeRegExp(url)})`, 'g');
          const newArray = linkText.split(regex);
          return newArray.map(value => {
            if (link && value === url && (thirdParty ? value === link.initialUrl : value === link.url)) {
              const result = jsx('element', { type: 'link', ...link }, [{ text: '' }]);
              if (!typeGuards.isCustomLinkGuard(result)) throw new Error('error creating custom link');
              return result;
            } else {
              return jsx('text', { type: 'text' }, [{ text: value }]);
            }
          });
        } else if (mention && mentions.length) {
          const newArray = isTwitter ? child.text.split(/(@\w+)/g) : child.text.split(/(@\[\w+])/g);
          return newArray.map(value => {
            if (value.includes('@')) {
              const rx = isTwitter ? /@(.*)/g : /@\[(.*)]/g;
              const matchArr = rx.exec(value);
              const externalId = matchArr && matchArr[1];
              if (externalId) {
                const matchedMention = mentions.find(item => item.externalId === externalId);
                if (matchedMention) {
                  const result = jsx(
                    'element',
                    {
                      type: 'mention',
                      ...matchedMention
                    },
                    [{ text: '' }]
                  );
                  if (!typeGuards.isCustomMentionGuard(result)) throw new Error('Error creating CustomMention');
                  return result;
                }
              }
            }
            return jsx('text', { type: 'text' }, [{ text: value }]);
          });
        }
      }
      return child;
    });
    p.children = children.flatMap(c => c);
    return p;
  });
};

export const serializeText = (text: Array<CustomSlateElement | CustomText>): string => {
  return text
    .map(p => {
      if (Text.isText(p)) {
        return p.text;
      } else if (Element.isElement(p) && p.type === 'mention') {
        const { method } = p;
        return method === 'TWITTER_USER' ? `@${p.username}` : `@[${p.externalId}]`;
      } else if (Element.isElement(p) && p.type === 'link') {
        return DOMPurify.sanitize(p.initialUrl);
      } else {
        if (!typeGuards.isCustomSlateElementArray(p.children)) {
          throw new Error('paragraph children are not of type CustomSlateElement.');
        }
        return `${serializeText(p.children)}\n`;
      }
    })
    .join('')
    .trim();
};

export const serializeTextToHtml = (
  text: CustomSlateElement[],
  socialNetwork?: SocialNetworksEnum,
  pClasses?: string
): string => {
  let classValue = '';
  if (socialNetwork) {
    classValue = `${socialNetwork}-text`;
  }
  if (pClasses) {
    classValue = classValue ? `${classValue} ${pClasses}` : pClasses;
  }
  const emptyP = `<p ${classValue ? 'class="' + classValue + '"' : ''}>&nbsp;</p>`;
  const emptyParRegex = new RegExp(`^(${emptyP}|\\s+${emptyP})+|(${emptyP}|${emptyP}\\s+)+$`, 'g');
  return text
    .map(p => {
      if (Text.isText(p)) {
        return DOMPurify.sanitize(p.text);
      } else if (Element.isElement(p) && p.type === 'mention') {
        const result = socialNetwork === SocialNetworksEnum.twitter ? p.username : p.displayName;
        return `<span class="mention">@${result}</span>`;
      } else if (Element.isElement(p) && p.type === 'link') {
        return `<a style="pointer-events: none" href="#">${p.url}</a>`;
      } else {
        if (!typeGuards.isCustomSlateElementArray(p.children))
          throw new Error('paragraph children are not aof type CustomSlateElement.');
        return `<p ${classValue ? 'class="' + classValue + '"' : ''}>${
          serializeTextToHtml(p.children, socialNetwork) || '&nbsp;'
        }</p>`;
      }
    })
    .join('')
    .replace(emptyParRegex, '');
};

type SerializeType = 'queue' | 'd&d';

const getDisplayLink = (link: CustomLink, type: SerializeType): string => {
  switch (type) {
    case 'd&d':
      return link.initialUrl;
    case 'queue':
      return link.url;
  }
};

const textMapper = (p: CustomSlateElement, type: SerializeType): string => {
  if (Text.isText(p)) {
    return ` ${DOMPurify.sanitize(p.text)}`;
  } else if (Element.isElement(p) && p.type === 'mention') {
    const { method } = p;
    return ` <span class="mention">@${
      method === ThirdPartyProfileSearchType.TWITTER_USER ? p.username : p.displayName
    }</span>`;
  } else if (Element.isElement(p) && p.type === 'link') {
    return ` <a style="pointer-events: none" href="#">${getDisplayLink(p, type)}</a>`;
  } else {
    if (!typeGuards.isCustomSlateElementArray(p.children))
      throw new Error('paragraph children are not aof type CustomSlateElement.');
    switch (type) {
      case 'queue':
        return serializeTextForQueue(p.children);
      case 'd&d':
        return `<p>${serializeTextForDragAndDrop(p.children)}</p>`;
    }
  }
};
export const serializeTextForQueue = (text: CustomSlateElement[]): string => {
  return text
    .map(p => {
      return textMapper(p, 'queue');
    })
    .join('')
    .replace(emptyCharRegex, '');
};

export const serializeTextForDragAndDrop = (text: CustomSlateElement[]): string => {
  return text
    .map(p => {
      return textMapper(p, 'd&d');
    })
    .join('');
  // .replace(emptyCharRegex, '');
};

// TODO add unit test for all serializer helpers.
